import { router } from '@inertiajs/react';
import axios from 'axios';
import { Loader2, Pencil, Share2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { SheetPickerList, type SheetPickerOption } from '@/components/global/SheetPickerList';
import DataPointInfoButton from '@/components/ui/analytics/DataPointInfoButton';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { EmptyState } from '@/components/ui/shadcn/empty-state';
import { useTranslations } from '@/hooks/useTranslations';
import type { AnalyticsDateRange } from '@/types/analytics';
import type { DashboardListItem, ReportRegistry, WidgetConfig, WidgetResult } from '@/types/dashboards';

import DashboardBuilder from './DashboardBuilder';
import WidgetCanvas from './WidgetCanvas';
import WidgetChart from './WidgetChart';
import { useDashboardData } from './useDashboardData';

interface CustomDashboardViewProps {
    /** The board as it appears in the analytics dropdown, including who owns it. */
    summary: DashboardListItem;
    registry: ReportRegistry;
    dateRange: AnalyticsDateRange;
    /** Bubbles up when the dashboard is edited + saved, so the parent can refresh the dropdown. */
    onUpdated?: (dashboard: DashboardListItem) => void;
}

export default function CustomDashboardView({ summary, registry, dateRange, onUpdated }: CustomDashboardViewProps) {
    const { t } = useTranslations();
    const dashboardId = summary.id;
    const [editing, setEditing] = useState(false);
    const [sharing, setSharing] = useState(false);
    const { data, loading, error } = useDashboardData(dashboardId, dateRange, !editing);

    const resultsById = useMemo(() => {
        const map: Record<string, WidgetResult> = {};
        for (const widget of data?.widgets ?? []) {
            map[widget.id] = widget.result;
        }
        return map;
    }, [data]);

    if (editing && data) {
        // Hand the builder just the widget configs (drop the computed result) —
        // works for both single and comparison shapes.
        const widgetConfigs: WidgetConfig[] = data.widgets.map((widget) => {
            const clone = { ...widget } as Record<string, unknown>;
            delete clone.result;
            return clone as unknown as WidgetConfig;
        });

        return (
            <DashboardBuilder
                registry={registry}
                dateRange={dateRange}
                initial={{ id: dashboardId, name: data.dashboard.name, widgets: widgetConfigs }}
                onSaved={(dashboard) => {
                    setEditing(false);
                    onUpdated?.({ ...summary, ...dashboard });
                }}
                onCancel={() => setEditing(false)}
            />
        );
    }

    if (loading && !data) {
        return (
            <div className="text-muted-foreground flex items-center justify-center gap-2 py-24 text-sm">
                <Loader2 className="h-5 w-5 animate-spin" />
                {t('dashboards.builder.loading', 'Loading dashboard…')}
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="text-danger-subtle-foreground py-24 text-center text-sm">
                {t('dashboards.builder.load_failed', 'Could not load this dashboard.')}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                    <h2 className="truncate text-xl font-semibold">{data.dashboard.name}</h2>
                    {!summary.is_owner && summary.owner_name && (
                        <Badge variant="secondary">{t('dashboards.sharing.shared_by', 'Shared by :name').replace(':name', summary.owner_name)}</Badge>
                    )}
                </div>

                {summary.can_manage ? (
                    <div className="flex shrink-0 items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setSharing(true)}>
                            <Share2 className="mr-2 h-4 w-4" />
                            {t('dashboards.sharing.trigger', 'Share')}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            {t('dashboards.builder.edit', 'Edit')}
                        </Button>
                    </div>
                ) : (
                    <span className="text-muted-foreground shrink-0 text-sm">
                        {t('dashboards.sharing.view_only', 'You have view-only access to this dashboard.')}
                    </span>
                )}
            </div>

            {data.widgets.length === 0 ? (
                <EmptyState
                    title={t('dashboards.builder.empty_title', 'This dashboard has no widgets yet')}
                    description={t('dashboards.builder.empty_description', 'Edit the dashboard to add your first widget.')}
                />
            ) : (
                <WidgetCanvas
                    widgets={data.widgets}
                    renderBody={(widget) => <WidgetChart result={resultsById[widget.id] ?? { kind: 'error' }} />}
                    // The engine ships each widget's own explanation with its result,
                    // so a recipient of a shared board can see exactly what it queried.
                    renderInfo={(widget) => <DataPointInfoButton doc={resultsById[widget.id]?.doc} />}
                />
            )}

            {summary.can_manage && <ShareDashboardModal dashboardId={dashboardId} open={sharing} onOpenChange={setSharing} />}
        </div>
    );
}

interface SharePerson {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    role?: string | null;
}

interface ShareCandidatesResponse {
    people: SharePerson[];
    shared_user_ids: string[];
}

/**
 * Read-only sharing: pick colleagues, they get the board in their "Shared with me"
 * group. Same shape as the Imitate sheet — a searchable list in the sheet body
 * grouped by role, rather than a combobox opening over the overlay.
 */
function ShareDashboardModal({ dashboardId, open, onOpenChange }: { dashboardId: string; open: boolean; onOpenChange: (open: boolean) => void }) {
    const { t } = useTranslations();
    const [candidates, setCandidates] = useState<ShareCandidatesResponse | null>(null);
    const [sharedIds, setSharedIds] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    // Fetched once per opening; the picker filters the list without a round trip.
    useEffect(() => {
        if (!open) {
            return;
        }

        let cancelled = false;

        void (async () => {
            setLoading(true);
            try {
                const { data } = await axios.get<ShareCandidatesResponse>(`/api/dashboards/${dashboardId}/share-candidates`);
                if (cancelled) {
                    return;
                }
                setCandidates(data);
                setSharedIds(data.shared_user_ids);
            } catch {
                if (!cancelled) {
                    toast.error(t('dashboards.sharing.load_failed', 'Could not load the list of colleagues.'));
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, dashboardId, t]);

    /** One heading per role so the list reads by team; the roleless sit last. */
    const options = useMemo<SheetPickerOption[]>(() => {
        const noRole = t('dashboards.sharing.no_role', 'No role');

        return [...(candidates?.people ?? [])]
            .map((person) => ({ person, group: person.role?.trim() ? person.role.trim() : noRole }))
            .sort((a, b) => {
                if (a.group === b.group) {
                    return 0;
                }
                if (a.group === noRole) {
                    return 1;
                }
                if (b.group === noRole) {
                    return -1;
                }
                return a.group.localeCompare(b.group);
            })
            .map(({ person, group }) => ({
                id: person.id,
                label:
                    [person.first_name, person.last_name].filter(Boolean).join(' ').trim() ||
                    person.email ||
                    t('dashboards.sharing.unnamed', 'Unnamed user'),
                description: person.email ?? undefined,
                keywords: person.role ?? undefined,
                badge: person.role ? <Badge variant="secondary">{person.role}</Badge> : null,
                group,
            }));
    }, [candidates, t]);

    // One add or one remove per toggle — firing both would race on the pivot.
    const syncShares = (next: string[]) => {
        const added = next.find((id) => !sharedIds.includes(id));
        const removed = sharedIds.find((id) => !next.includes(id));

        const request = added
            ? axios.post(`/api/dashboards/${dashboardId}/shares`, { user_id: added })
            : removed
              ? axios.delete(`/api/dashboards/${dashboardId}/shares/${removed}`)
              : null;

        if (!request) {
            return;
        }

        const previous = sharedIds;
        setSharedIds(next);

        request
            .then(({ data }) => {
                setSharedIds(data.shared_user_ids ?? next);
                toast.success(added ? t('dashboards.sharing.shared', 'Dashboard shared.') : t('dashboards.sharing.unshared', 'Access removed.'));
                router.reload({ only: ['userDashboards'] });
            })
            .catch(() => {
                setSharedIds(previous);
                toast.error(t('dashboards.sharing.failed', 'Could not update sharing.'));
            });
    };

    return (
        <ResponsiveModal open={open} onOpenChange={onOpenChange} variant="sheet">
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('dashboards.sharing.title', 'Share this dashboard')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t(
                            'dashboards.sharing.description',
                            'Colleagues you add can view this dashboard. Only you can rename it, change its widgets, or delete it.',
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody>
                    {!loading && options.length === 0 ? (
                        <p className="text-muted-foreground py-8 text-center text-sm">
                            {t('dashboards.sharing.no_candidates', 'Nobody else has access to analytics yet, so there is no one to share with.')}
                        </p>
                    ) : (
                        <SheetPickerList
                            multiple
                            values={sharedIds}
                            onValuesChange={syncShares}
                            options={options}
                            loading={loading && options.length === 0}
                            labels={{
                                search: t('dashboards.sharing.search', 'Search colleagues by name or email…'),
                                empty: t('dashboards.sharing.empty', 'No matching colleagues.'),
                            }}
                        />
                    )}
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('common.close', 'Close')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
